// biome-ignore-all lint/correctness/useHookAtTopLevel: false positive — Biome's component detection misses this generic forwardRef component, so it flags every (genuinely top-level) hook call.
/**
 * Indigo DropdownSelector for the design system.
 * A customizable dropdown selector built on top of Ant Design Select.
 *
 * Features:
 * - Generic typing for values
 * - Single and multiple selection modes
 * - Loading states
 * - Error states
 * - Customizable filtering
 * - Smart width calculation
 * - Support for null values
 * - Text-only display in display mode
 * - Dimmed appearance in disabled state
 *
 * Usage:
 *   <DropdownSelector
 *     options={[{value: 1, label: "Option 1"}, {value: 2, label: "Option 2"}]}
 *     value={selectedValue}
 *     onChange={handleChange}
 *   />
 *
 *   // Display mode shows only text value
 *   <DropdownSelector
 *     options={[{value: 1, label: "Option 1"}, {value: 2, label: "Option 2"}]}
 *     value={1}
 *     onChange={handleChange}
 *     display={true}
 *   />
 *
 *   // Disabled state dims the component but keeps structure
 *   <DropdownSelector
 *     options={[{value: 1, label: "Option 1"}, {value: 2, label: "Option 2"}]}
 *     value={1}
 *     onChange={handleChange}
 *     disabled={true}
 *   />
 */
import { Select as AntSelect } from "antd";
import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";
import {
  ComponentRef,
  FC,
  ForwardedRef,
  forwardRef,
  ReactElement,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  Ref,
  RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { followCursor } from "tippy.js";
import { hasActiveTextSelection } from "~/utils/textInputUtils";
import { Icon } from "../Icons/Icons";
import { LabelBlock } from "../LabelBlock";
import { Skeleton } from "../Skeleton";
import { Tag } from "../Tag";
import { Tooltip } from "../Tooltip";
import "./DropdownSelector.css";

export interface Option<T> {
  value: T;
  label: string;
  /** When true, the option is shown but not selectable. */
  disabled?: boolean;
  /** Tooltip shown when hovering a disabled option. */
  disabledTooltip?: string;
  /** When true, renders a DEFAULT tag next to this option. */
  isDefault?: boolean;
  /**
   * Small tag rendered next to the label in the open menu (e.g. "Current",
   * "Inactive"). A general form of `isDefault`; both may be set.
   */
  tag?: OptionTag;
  /**
   * Starts a section: this option and every following one (until the next
   * `sectionLabel`) render under a muted, non-selectable header with a
   * divider — an antd option group built from a flat list. A selected option
   * floated to the top by `selectedFirst` leaves its section behind; the
   * header stays with the rest.
   */
  sectionLabel?: string;
  /**
   * Rendered as a muted number at the right edge of the option row in the
   * open menu only (e.g. how many items a filter option matches). The
   * selected-value tag keeps the plain label.
   */
  count?: number;
  /**
   * Upcoming value of `count`. When it differs, the row shows
   * "count → nextCount" (e.g. an unsaved edit changes what the option will
   * match once saved).
   */
  nextCount?: number;
}

export interface OptionTag {
  label: string;
  /** Tag colour; `info` matches the DEFAULT tag, `neutral` is muted. */
  status?: "info" | "neutral" | "success" | "warning" | "error";
}

export type DropdownWidth = "auto" | "fixed" | "full";

export interface DropdownSelectorProps<T>
  extends VariantProps<typeof dropdownVariants> {
  /**
   * Array of selectable options with values and labels
   */
  options: Option<T>[];

  /**
   * Current selected value(s)
   */
  value: T | T[] | null;

  /**
   * Width of the dropdown
   * - "auto": Automatically adjusts based on content
   * - "fixed": Fixed width of 320px
   * - "full": Takes full width of container
   * @default "auto"
   */
  width?: "auto" | "fixed" | "full";

  /**
   * Visual style variant of the dropdown
   */
  variant?: "outline" | "naked" | "filled" | "elevated";

  /**
   * Size of the dropdown
   * - "small": 22px height
   * - "default": 32px height
   * - "large": 40px height
   * @default "default"
   */
  size?: "small" | "default" | "large";

  /**
   * Callback fired when selection changes
   */
  onChange: ((value: T | null) => void) | ((value: T[] | null) => void);

  /**
   * Callback fired when dropdown gets focus
   */
  onFocus?: () => void;

  /**
   * Initial value for uncontrolled usage
   */
  defaultValue?: T | T[] | null;

  /**
   * Enable multiple selection mode
   */
  multiple?: boolean;

  /**
   * Disable the dropdown (makes it dim but keeps structure)
   */
  disabled?: boolean;

  /**
   * Display mode - shows only text value without dropdown structure
   */
  display?: boolean;

  /**
   * Additional CSS class
   */
  className?: string;

  /**
   * Placeholder text when nothing is selected
   */
  placeholder?: string;

  /**
   * When true, selected value tag is prefixed with placeholder text (e.g. "Panel: Completed")
   * @default false
   */
  showPlaceholderInSelectedValue?: boolean;

  /**
   * Show loading state
   */
  isSaving?: boolean;

  /**
   * The option list is still being fetched. In display mode a value with no
   * matching option yet renders a skeleton instead of the raw value — for
   * entity-backed dropdowns (physician, MA, location) that raw value is a bare
   * UUID, which briefly leaks into the page while the lists load.
   */
  isLoadingOptions?: boolean;

  /**
   * Show error state
   */
  isError?: boolean;

  /**
   * How to filter options when typing: "prefix" matches beginning of string, "contains" matches anywhere in string
   */
  filterType?: "prefix" | "contains";

  /**
   * Whether typing in the trigger filters the options. Turn off for short,
   * fixed option sets where the caret reads as an editable text field.
   * @default true
   */
  showSearch?: boolean;

  /**
   * Custom filter function for advanced search scenarios.
   * Takes the search input and option value, returns true if the option should be shown.
   * When provided, this overrides the default label-based filtering.
   */
  customFilterFn?: (input: string, optionValue: T) => boolean;

  /**
   * For multiple selection, max number of tags to show before collapsing
   */
  maxSelectedVisible?: number;

  /**
   * Allow null/empty value selection
   */
  allowNull?: boolean;

  /**
   * Label to display for null value
   */
  nullLabel?: string;

  /**
   * Float currently-selected options to the top of the menu so the user can
   * see everything that's selected and unselect it easily. Ordering is frozen
   * while the menu is open (options only re-sort to the top on the next open)
   * so items don't jump around as you toggle them. Most useful for `multiple`.
   * @default false
   */
  selectedFirst?: boolean;

  /**
   * Show clear icon to reset the selection
   */
  allowClear?: boolean;

  /**
   * Reference to container element for dropdown positioning
   */
  popupContainerRef?: RefObject<HTMLElement>;

  /**
   * Test ID for dropdown element
   */
  dropdownTestId?: string;

  /**
   * Test ID for component
   */
  "data-testid"?: string;

  /**
   * Label text to display above the dropdown
   */
  label?: string;

  /**
   * Description text to display with the dropdown
   */
  description?: string;

  /**
   * Width percentage for the label block
   * @default "100%"
   */
  widthPercent?: string;

  /**
   * Show "Add new" option at the end of the list
   * @default false
   */
  showAddNew?: boolean;

  /**
   * Callback fired when "Add new" option is clicked
   */
  onAddNew?: () => void;

  /**
   * Custom text for "Add new" option
   * @default "Add new"
   */
  addNewLabel?: string;

  /**
   * Custom text for empty state (no results found)
   * @default "No results found."
   */
  emptyText?: string;

  /**
   * Optional action menu component (e.g., ThreeDotDropdownMenu)
   * Rendered next to the label when provided
   */
  actionMenu?: ReactNode;
  /**
   * Rendered inline right after the selected value and before the external-link
   * icon — e.g. a badge qualifying the selected entity. Independent of the
   * label-level `actionMenu`.
   */
  valueAdornment?: ReactNode;
  /**
   * External link URL - when provided in display mode, shows a link-out icon button next to the value
   */
  externalLink?: string;

  /**
   * Custom icon to display instead of the default dropdown arrow
   */
  customIcon?: ReactNode;

  /** Remove default padding from LabelBlock when true */
  noPadding?: boolean;

  /**
   * Tooltip text shown on hover of a "?" icon next to the label (edit mode only).
   */
  infoTooltip?: string;

  /**
   * Whether the block is in a warning state (amber label / border / description)
   * @default false
   */
  isWarning?: boolean;
}

/**
 * dropdownVariants - CVA configuration for DropdownSelector component variants
 * Uses CSS classes that map to our design tokens
 */
const dropdownVariants = cva("indigo-dropdown-select-base", {
  variants: {
    variant: {
      outline: "indigo-dropdown-select--outline", // Default style with border
      naked: "indigo-dropdown-select--naked", // No background, no border, left padding 0
      filled: "indigo-dropdown-select--filled", // With background but no border, use in forms
      elevated: "indigo-dropdown-select--elevated", // With border, background and shadow
    },
    width: {
      auto: "auto",
      fixed: "fixed",
      full: "full",
    },
    size: {
      small: "indigo-dropdown-select--small",
      default: "indigo-dropdown-select--default",
      large: "indigo-dropdown-select--large",
    },
  },
  defaultVariants: {
    variant: "outline",
    width: "full",
    size: "default",
  },
});

/**
 * DropdownArrow Component
 *
 * A reusable dropdown arrow icon with animation for open/closed states
 *
 * @param isOpen - Whether the dropdown is currently open
 * @param disabled - Whether the dropdown is disabled
 * @param isError - Whether the dropdown is in an error state
 * @returns A React component for the dropdown arrow
 */
const DropdownArrow: FC<{
  isOpen: boolean;
  disabled?: boolean;
  isError?: boolean;
}> = ({ isOpen, disabled, isError }) => {
  const arrowClassName = clsx("indigo-dropdown-select__arrow", {
    "is-open": isOpen,
    "is-disabled": disabled,
    "is-error": isError,
  });

  return (
    <div className="indigo-dropdown-select__arrow-container">
      <Icon name="down-chevron" size="s" className={arrowClassName} />
    </div>
  );
};

// Opaque identifiers (UUIDs) are what entity-backed dropdowns store as their
// value. When one can't be matched to an option it means nothing to the reader,
// so it gets replaced by MISSING_VALUE_LABEL rather than printed. Values in any
// other shape (enum codes, free text) still read as themselves and are left be.
const OPAQUE_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const MISSING_VALUE_LABEL = "Unavailable";

// Cap on how wide the open popup may grow to fit long option labels.
// Triggers are often kept intentionally narrow (e.g. dashboard filters), so we
// let the menu expand past the trigger up to this width; longer labels still
// ellipsize (and expose their full text via the per-option tooltip).
const DROPDOWN_POPUP_MAX_WIDTH = 360;

// Create a cached canvas for text width measurement
const cachedCanvas: HTMLCanvasElement = document.createElement("canvas");

/**
 * Measures the pixel width of a given text string using the specified font.
 * Utilizes a single cached off-screen canvas element for efficiency, so all calls
 * (and all DropdownSelector component instances) share the same canvas. This avoids DOM manipulation
 * and is much faster than creating temporary DOM elements for measurement.
 *
 * @param text - The text string to measure.
 * @param font - The CSS font property string (e.g., "14px Inter, sans-serif").
 * @returns The width of the text in pixels.
 */
const measureTextWidth = (text: string, font = "14px Inter, sans-serif") => {
  const context = cachedCanvas.getContext("2d");
  if (!context) return 0;
  context.font = font;
  return context.measureText(text).width;
};

interface PrefixedTagRenderProps {
  label: ReactNode;
  value: unknown;
  closable: boolean;
  onClose: (event: ReactMouseEvent<HTMLElement>) => void;
}

const getPrefixedTagLabel = (
  label: ReactNode,
  tagValue: unknown,
  placeholder: string,
  firstSelectedValue: unknown,
) => {
  const shouldPrefixFirstOnly =
    firstSelectedValue !== null && Object.is(tagValue, firstSelectedValue);

  return shouldPrefixFirstOnly && placeholder
    ? `${placeholder}: ${String(label)}`
    : String(label);
};

const renderPrefixedTag = ({
  label,
  value: tagValue,
  closable,
  onClose,
  placeholder,
  firstSelectedValue,
}: PrefixedTagRenderProps & {
  placeholder: string;
  firstSelectedValue: unknown;
}): ReactElement => {
  const prefixedLabel = getPrefixedTagLabel(
    label,
    tagValue,
    placeholder,
    firstSelectedValue,
  );

  return (
    <span
      className="ant-select-selection-item indigo-prefixed-tag"
      title={prefixedLabel}
    >
      <span className="ant-select-selection-item-content indigo-prefixed-tag__content">
        {prefixedLabel}
      </span>
      {closable && (
        <span
          className="ant-select-selection-item-remove"
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={onClose}
        >
          ×
        </span>
      )}
    </span>
  );
};

const renderOptionLabel = (
  label: ReactNode,
  isDefault?: boolean,
  count?: number,
  nextCount?: number,
  tag?: OptionTag,
) => (
  <span className="indigo-dropdown-option-content">
    <span className="indigo-dropdown-option-label">{label}</span>
    {count !== undefined && (
      <span className="indigo-dropdown-option-count">
        {count}
        {nextCount !== undefined && nextCount !== count && (
          <>
            {" → "}
            <span className="indigo-dropdown-option-count-next">
              {nextCount}
            </span>
          </>
        )}
      </span>
    )}
    {isDefault && (
      <Tag
        label="DEFAULT"
        variant="solid"
        status="info"
        shape="square"
        size="small"
      />
    )}
    {tag && (
      <Tag
        label={tag.label}
        // Neutral reads as a muted chip (light grey on grey); every other
        // status is the accent-filled chip (white on colour).
        variant={tag.status === "neutral" ? "solid" : "filled"}
        status={tag.status ?? "info"}
        shape="square"
        size="small"
      />
    )}
  </span>
);

// Create a functional component for our dropdown
function InternalDropdownSelector<T>(
  {
    options,
    value,
    onChange,
    onFocus,
    defaultValue,
    multiple = false,
    disabled = false,
    display = false,
    className = "",
    placeholder = multiple ? "Select" : "Select one",
    showPlaceholderInSelectedValue = false,
    isSaving = false,
    isLoadingOptions = false,
    isError,
    filterType = "contains",
    showSearch = true,
    customFilterFn,
    maxSelectedVisible,
    allowNull = false,
    nullLabel = "-",
    selectedFirst = false,
    allowClear = false,
    popupContainerRef,
    dropdownTestId,
    variant = "filled",
    width = "full",
    size = "default",
    "data-testid": dataTestId,
    label,
    description,
    widthPercent,
    showAddNew = false,
    onAddNew,
    addNewLabel = "Add new",
    emptyText = "No results found.",
    actionMenu,
    valueAdornment,
    externalLink,
    customIcon,
    noPadding,
    infoTooltip,
    isWarning,
  }: DropdownSelectorProps<T>,
  ref: ForwardedRef<ComponentRef<typeof AntSelect>>,
) {
  const [isOpen, setIsOpen] = useState(false);
  // Snapshot of the selected values taken when the menu opens, used to freeze
  // the "selected first" ordering while open (see orderedOptions below).
  const [openSelectedSnapshot, setOpenSelectedSnapshot] =
    useState<Set<string> | null>(null);
  const [placeholderWidth, setPlaceholderWidth] = useState<number>(0);
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);
  const selectRef = useRef<ComponentRef<typeof AntSelect>>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Merge the forwarded ref with our local ref
  const mergedRef = useMemo(() => (ref ? ref : selectRef), [ref]);

  const optionsWithNull = useMemo(() => {
    const base =
      allowNull && !multiple
        ? [{ value: null as unknown as T, label: nullLabel }, ...options]
        : options;
    // Suppress rc-select's auto-generated `title` attribute on the option row
    // so it doesn't race our indigo Tooltip (rendered in `optionRender`) and
    // produce a duplicate native-browser tooltip.
    return base.map((o) => ({ ...o, title: "" }));
  }, [allowNull, nullLabel, options, multiple]);

  const optionsByValue = useMemo(() => {
    const map = new Map<string, Option<T>>();
    optionsWithNull.forEach((o) => map.set(String(o.value), o));
    return map;
  }, [optionsWithNull]);

  // Set of currently-selected values (stringified for lookup).
  const selectedKeys = useMemo(() => {
    const values = Array.isArray(value)
      ? value
      : value !== null && value !== undefined
        ? [value]
        : [];
    return new Set(values.map(String));
  }, [value]);

  // Options with the selected ones floated to the top (opt-in via selectedFirst).
  // While open we sort by the snapshot taken at open time so toggling an option
  // doesn't make rows jump; while closed we track the live selection so the next
  // open renders correctly sorted on first paint (no flash).
  const { orderedOptions, floatedCount } = useMemo(() => {
    const orderingKeys =
      isOpen && openSelectedSnapshot ? openSelectedSnapshot : selectedKeys;
    if (!selectedFirst || orderingKeys.size === 0)
      return { orderedOptions: optionsWithNull, floatedCount: 0 };

    const selected: typeof optionsWithNull = [];
    const rest: typeof optionsWithNull = [];
    for (const option of optionsWithNull) {
      (orderingKeys.has(String(option.value)) ? selected : rest).push(option);
    }
    return {
      orderedOptions: [...selected, ...rest],
      floatedCount: selected.length,
    };
  }, [
    selectedFirst,
    isOpen,
    openSelectedSnapshot,
    selectedKeys,
    optionsWithNull,
  ]);

  // Which section each option belongs to, from the canonical order: an
  // option inherits the last `sectionLabel` seen before it.
  const sectionByValue = useMemo(() => {
    const map = new Map<string, string | undefined>();
    let current: string | undefined;
    for (const option of optionsWithNull) {
      if (option.sectionLabel) current = option.sectionLabel;
      map.set(String(option.value), current);
    }
    return map;
  }, [optionsWithNull]);

  // What antd renders: the flat list, or — when any option starts a section
  // — floated selections first (ungrouped), then the rest folded into option
  // groups by section. A group header is not an option, so it takes no hover
  // and cannot be picked.
  type MenuOption = (typeof optionsWithNull)[number];
  type MenuGroup = { label: string; key: string; options: MenuOption[] };
  const antOptions = useMemo<(MenuOption | MenuGroup)[]>(() => {
    if (!optionsWithNull.some((option) => option.sectionLabel)) {
      return orderedOptions;
    }
    const out: (MenuOption | MenuGroup)[] = orderedOptions.slice(
      0,
      floatedCount,
    );
    let group: MenuGroup | null = null;
    let groupLabel: string | undefined;
    for (const option of orderedOptions.slice(floatedCount)) {
      const section = sectionByValue.get(String(option.value));
      if (section !== groupLabel || group === null) {
        groupLabel = section;
        group = section
          ? { label: section, key: `section:${section}`, options: [] }
          : null;
        if (group) out.push(group);
      }
      if (group) group.options.push(option);
      else out.push(option);
    }
    return out;
  }, [optionsWithNull, orderedOptions, floatedCount, sectionByValue]);

  useLayoutEffect(() => {
    // Use canvas to calculate placeholder width
    const font = "var(--indigo-dropdown-font-size) var(--indigo-font-body)";
    const pWidth = measureTextWidth(placeholder, font);
    setPlaceholderWidth(pWidth + 60);
  }, [placeholder]);

  const handleOnChange = (value: T | T[]) => {
    if (multiple) {
      (onChange as (value: T[] | null) => void)(value as T[]);
    } else {
      (onChange as (value: T | null) => void)(value as T);
    }
  };

  const filterOption = (
    input: string,
    option?: { label: string; value: T },
  ) => {
    if (!option) return false;

    // Use custom filter function if provided
    if (customFilterFn) {
      return customFilterFn(input, option.value);
    }

    // Default label-based filtering
    return filterType === "prefix"
      ? option.label.toLowerCase().startsWith(input.toLowerCase())
      : option.label.toLowerCase().includes(input.toLowerCase());
  };

  const displayValue = useMemo(() => {
    if (allowNull && value === null) {
      return null;
    } else if (value === undefined || value === null) {
      return undefined;
    } else {
      return value;
    }
  }, [allowNull, value]);

  const isValueSelected = multiple
    ? Array.isArray(value) && value.length > 0
    : value !== null && value !== undefined;

  const firstSelectedValue =
    multiple && Array.isArray(value) && value.length > 0 ? value[0] : null;

  const handlePrefixedTagRender = (tagProps: PrefixedTagRenderProps) => {
    return renderPrefixedTag({
      ...tagProps,
      placeholder,
      firstSelectedValue,
    });
  };

  const selectedValues = useMemo<T[]>(
    () =>
      Array.isArray(value)
        ? value
        : value === null || value === undefined
          ? []
          : [value],
    [value],
  );

  // A value resolves when an option carries a label for it. An option labelled
  // with the value itself doesn't count: some option builders synthesize one so
  // the selection survives a save, and it says no more than the bare value does.
  const resolveLabel = useCallback(
    (selected: T): string | null => {
      const label = options.find((option) => option.value === selected)?.label;
      return !label || label === String(selected) ? null : label;
    },
    [options],
  );

  const unresolvedValues = useMemo(
    () => selectedValues.filter((selected) => resolveLabel(selected) === null),
    [resolveLabel, selectedValues],
  );

  // Nothing to render but the raw value until the options land — show a
  // placeholder for that window instead of flashing a UUID.
  const isResolvingDisplayValue =
    isLoadingOptions && unresolvedValues.length > 0;

  // An opaque ID that is still unresolved once the options have loaded points
  // at a record that no longer exists. Name that state instead of printing the
  // UUID, and keep the UUID reachable through the adjacent info tooltip.
  const missingIdValues = useMemo(
    () =>
      isLoadingOptions
        ? []
        : unresolvedValues.filter((selected) =>
            OPAQUE_ID_PATTERN.test(String(selected)),
          ),
    [isLoadingOptions, unresolvedValues],
  );

  const hasMissingIdValues = missingIdValues.length > 0;
  const areAllValuesMissing =
    hasMissingIdValues && missingIdValues.length === selectedValues.length;

  const formatValue = useCallback(
    (selected: T): string =>
      resolveLabel(selected) ??
      (missingIdValues.includes(selected)
        ? MISSING_VALUE_LABEL
        : String(selected)),
    [missingIdValues, resolveLabel],
  );

  // Helper function to get display value text
  const getDisplayValue = () => {
    if (selectedValues.length === 0) {
      return nullLabel;
    }

    return selectedValues.map(formatValue).join(", ");
  };

  // Create the dropdown component
  const handleDisplayCopy = () => {
    if (hasActiveTextSelection()) {
      return;
    }

    const textToCopy = getDisplayValue();
    if (textToCopy && textToCopy !== nullLabel) {
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          setShowCopiedTooltip(true);
          setTimeout(() => setShowCopiedTooltip(false), 2000);
        })
        .catch(() => {
          // Silently fail if clipboard access is denied
        });
    }
  };

  const dropdownComponent = display ? (
    isResolvingDisplayValue ? (
      <Skeleton
        width="60%"
        className="indigo-dropdown-display-skeleton"
        data-testid={dataTestId ? `${dataTestId}-loading` : undefined}
      />
    ) : (
      // When in display mode, just show the text value
      <Tooltip
        content="Copied!"
        placement="top"
        size="sm"
        visible={showCopiedTooltip}
      >
        <div
          className={clsx("indigo-dropdown-display-text", {
            "indigo-dropdown-display-text--with-note": hasMissingIdValues,
          })}
          data-testid={dataTestId}
          onClick={handleDisplayCopy}
        >
          <span
            className={clsx({
              "indigo-dropdown-display-missing": areAllValuesMissing,
            })}
          >
            {getDisplayValue()}
          </span>
          {hasMissingIdValues && (
            <Tooltip
              content={
                <span className="indigo-dropdown-missing-tooltip">
                  {missingIdValues.length > 1
                    ? "These records no longer exist."
                    : "This record no longer exists."}
                  {missingIdValues.map((missing) => (
                    <span key={String(missing)}>ID: {String(missing)}</span>
                  ))}
                </span>
              }
              placement="top"
              size="sm"
              delay={200}
            >
              <span
                className="indigo-dropdown-missing-info"
                data-testid={
                  dataTestId ? `${dataTestId}-missing-info` : undefined
                }
                // The row copies its text on click; the icon only explains it.
                onClick={(event) => event.stopPropagation()}
              >
                <Icon name="functional-question" size="s" />
              </span>
            </Tooltip>
          )}
        </div>
      </Tooltip>
    )
  ) : (
    // Normal interactive dropdown
    <div
      ref={containerRef}
      className={clsx(
        "indigo-dropdown-select",
        dropdownVariants({ variant, width, size }),
        className,
        {
          "is-open": isOpen,
          "is-loading": isSaving,
          "is-disabled": disabled,
        },
      )}
    >
      <AntSelect
        mode={multiple ? "multiple" : undefined}
        value={displayValue}
        defaultValue={defaultValue}
        onChange={handleOnChange}
        onFocus={onFocus}
        disabled={disabled || isSaving}
        className={clsx(isValueSelected ? "has-value" : "no-value", {
          "show-placeholder-in-selected-value": showPlaceholderInSelectedValue,
        })}
        placeholder={placeholder}
        // Groups are a superset antd accepts; the prop's inferred option type
        // stays the flat one so `filterOption` / `optionRender` keep typing.
        options={antOptions as unknown as typeof orderedOptions}
        allowClear={allowClear}
        classNames={{
          popup: {
            root: "indigo-dropdown-menu",
          },
        }}
        onOpenChange={(visible) => {
          setIsOpen(visible);
          // Freeze the "selected first" ordering for the duration this menu
          // is open, based on the selection at open time.
          if (visible) setOpenSelectedSnapshot(selectedKeys);
        }}
        showSearch={showSearch}
        filterOption={filterType || customFilterFn ? filterOption : undefined}
        optionFilterProp="label"
        optionRender={(option) => {
          const origOption = optionsByValue.get(String(option.value));
          if (origOption?.disabled && origOption.disabledTooltip) {
            return (
              <Tooltip
                content={origOption.disabledTooltip}
                placement="right"
                size="md"
                delay={0}
              >
                <span className="indigo-dropdown-option-disabled">
                  {renderOptionLabel(
                    option.label,
                    origOption.isDefault,
                    origOption.count,
                    origOption.nextCount,
                    origOption.tag,
                  )}
                </span>
              </Tooltip>
            );
          }
          // Tooltip exposes the full label when the option row is narrower
          // than the text (e.g. long filenames in the file filter).
          // `followCursor` anchors it near the mouse instead of the far
          // edge of a wide inline span.
          if (!origOption?.label) return option.label;
          return (
            <Tooltip
              content={origOption.label}
              placement="top"
              size="sm"
              delay={200}
              followCursor="initial"
              plugins={[followCursor]}
            >
              {renderOptionLabel(
                option.label,
                origOption.isDefault,
                origOption.count,
                origOption.nextCount,
                origOption.tag,
              )}
            </Tooltip>
          );
        }}
        status={isError ? "error" : isWarning ? "warning" : ""}
        maxTagCount={maxSelectedVisible}
        maxTagPlaceholder={
          multiple && showPlaceholderInSelectedValue
            ? (omittedValues) => `+ ${omittedValues.length}...`
            : undefined
        }
        tagRender={
          multiple && showPlaceholderInSelectedValue
            ? handlePrefixedTagRender
            : undefined
        }
        notFoundContent={
          <div className="indigo-dropdown-empty">{emptyText}</div>
        }
        suffixIcon={
          multiple && isOpen ? undefined : isSaving ? (
            <div className="loading-spinner-container">
              <Icon name="spinner" className="loading-spinner" />
            </div>
          ) : (
            (customIcon ?? (
              <DropdownArrow
                isOpen={isOpen}
                disabled={disabled}
                isError={isError}
              />
            ))
          )
        }
        popupMatchSelectWidth={false}
        styles={{
          popup: {
            root: {
              // Grow to fit content, but never narrower than the trigger and
              // never wider than the cap. When the trigger is wider than the
              // cap (e.g. full-width dropdowns), min-width wins per CSS, so
              // those popups keep matching the trigger as before.
              minWidth: containerRef.current?.offsetWidth || "auto",
              maxWidth: DROPDOWN_POPUP_MAX_WIDTH,
            },
          },
        }}
        style={{
          minWidth:
            multiple && Array.isArray(value) && value.length
              ? 0
              : placeholderWidth,
        }}
        ref={mergedRef}
        getPopupContainer={
          popupContainerRef?.current
            ? () => popupContainerRef.current as HTMLElement
            : undefined
        }
        popupRender={(menu) => (
          <div data-testid={dropdownTestId}>
            {menu}
            {showAddNew && (
              <div
                className="indigo-dropdown-add-new"
                onClick={() => {
                  onAddNew?.();
                  setIsOpen(false);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onAddNew?.();
                    setIsOpen(false);
                  }
                }}
              >
                <Icon name="add" size="xs" />
                <span>{addNewLabel}</span>
              </div>
            )}
          </div>
        )}
        data-testid={dataTestId}
      />
    </div>
  );

  // If label or description is provided, wrap the dropdown in a LabelBlock
  if (label || description) {
    return (
      <LabelBlock
        label={label}
        description={description}
        isError={isError}
        isWarning={isWarning}
        disabled={disabled}
        widthPercent={widthPercent}
        actionMenu={actionMenu}
        valueAdornment={valueAdornment}
        externalLink={display ? externalLink : undefined}
        noPadding={noPadding}
        infoTooltip={infoTooltip}
      >
        {dropdownComponent}
      </LabelBlock>
    );
  }

  // Otherwise, return just the dropdown
  return dropdownComponent;
}

// Create the forwardRef version with correct typings
export const DropdownSelector = forwardRef(
  InternalDropdownSelector,
) as unknown as (<T>(
  props: DropdownSelectorProps<T> & {
    ref?: Ref<ComponentRef<typeof AntSelect>>;
  },
) => ReactElement) & { displayName: string };

// Now set displayName properly
DropdownSelector.displayName = "DropdownSelector";

export { dropdownVariants };
export default DropdownSelector;
